// Shared cinematic CSS keyframes and overlay components for arcade game menus
// THEME: Molten Gold / Ancient Lore
import { motion } from 'framer-motion';

// All keyframes used across cinematic menus
export const CINEMATIC_KEYFRAMES = `
  @keyframes scanline {
    0% { transform: translateY(-100%); }
    100% { transform: translateY(100vh); }
  }
  @keyframes glitchText {
    0%, 100% { text-shadow: -2px 0 #ff8c00, 2px 0 #ff4500; transform: translate(0); }
    20% { text-shadow: 2px 0 #ff8c00, -2px 0 #ff4500; transform: translate(-2px, 1px); }
    40% { text-shadow: -1px 0 #ffd700, 1px 0 #ff4500; transform: translate(1px, -1px); }
    60% { text-shadow: 3px 0 #ff8c00, -3px 0 #cc2200; transform: translate(2px, 0); clip-path: inset(40% 0 30% 0); }
    80% { text-shadow: -2px 0 #ffd700, 2px 0 #ff4500; transform: translate(-1px, 2px); clip-path: inset(10% 0 60% 0); }
  }
  @keyframes glitchFlicker {
    0%, 95%, 100% { opacity: 1; }
    96% { opacity: 0.8; transform: translate(-3px, 0); }
    97% { opacity: 1; transform: translate(2px, 0); }
    98% { opacity: 0.6; }
    99% { opacity: 1; transform: translate(0); }
  }
  @keyframes toxicDrip {
    0% { transform: translateY(-10px) scaleY(0.3); opacity: 0; }
    10% { opacity: 0.8; transform: translateY(0) scaleY(1); }
    50% { transform: translateY(60vh) scaleY(1.5); opacity: 0.5; }
    100% { transform: translateY(100vh) scaleY(2); opacity: 0; }
  }
  @keyframes gasBubble {
    0% { transform: translateY(0) scale(1); opacity: 0; }
    15% { opacity: 0.6; }
    50% { transform: translateY(-45vh) scale(1.3); opacity: 0.3; }
    100% { transform: translateY(-90vh) scale(0.5); opacity: 0; }
  }
  @keyframes terminalBlink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0; }
  }
  @keyframes pulseGlow {
    0%, 100% { box-shadow: 0 0 15px rgba(255,140,0,0.3), 0 0 30px rgba(255,140,0,0.1), inset 0 0 10px rgba(255,140,0,0.05); }
    50% { box-shadow: 0 0 25px rgba(255,140,0,0.5), 0 0 50px rgba(255,140,0,0.2), inset 0 0 20px rgba(255,140,0,0.1); }
  }
  @keyframes warningPulse {
    0%, 100% { opacity: 0.4; }
    50% { opacity: 0.8; }
  }
  @keyframes staticNoise {
    0% { background-position: 0 0; }
    10% { background-position: -5% -10%; }
    20% { background-position: -15% 5%; }
    30% { background-position: 7% -25%; }
    40% { background-position: -20% 25%; }
    50% { background-position: -15% 10%; }
    60% { background-position: 15% 0%; }
    70% { background-position: 0% 15%; }
    80% { background-position: 3% 35%; }
    90% { background-position: -10% 10%; }
    100% { background-position: 0 0; }
  }
  @keyframes pipeFlow {
    0% { background-position: 0 0; }
    100% { background-position: 0 200px; }
  }
  @keyframes neonPulse {
    0%, 100% { box-shadow: 0 0 10px rgba(255,140,0,0.3); }
    50% { box-shadow: 0 0 20px rgba(255,140,0,0.6); }
  }
  @keyframes slotSpin {
    0% { transform: translateY(0); }
    100% { transform: translateY(-33.33%); }
  }
`;

// CRT scanlines overlay — warm tint
export const ScanlineOverlay = () => (
  <div className="absolute inset-0 pointer-events-none z-50 overflow-hidden" style={{ mixBlendMode: 'overlay' }}>
    <div style={{
      position: 'absolute', inset: 0,
      background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.15) 2px, rgba(0,0,0,0.15) 4px)',
    }} />
    <div style={{
      position: 'absolute', inset: 0, width: '100%', height: '200%',
      background: 'linear-gradient(180deg, transparent 0%, rgba(255,140,0,0.03) 50%, transparent 100%)',
      animation: 'scanline 4s linear infinite',
    }} />
  </div>
);

// Static noise overlay
export const NoiseOverlay = () => (
  <div className="absolute inset-0 pointer-events-none z-40" style={{
    opacity: 0.04,
    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E")`,
    backgroundSize: '150px 150px',
    animation: 'staticNoise 0.5s steps(10) infinite',
  }} />
);

// Toxic gas bubbles rising from the bottom — molten palette
export const GasParticles = ({ count = 15, color = 'molten' }) => {
  const colors = {
    molten: ['rgba(255,215,0,0.2)', 'rgba(255,140,0,0.18)', 'rgba(255,69,0,0.12)'],
    green: ['rgba(255,215,0,0.2)', 'rgba(255,140,0,0.18)', 'rgba(255,69,0,0.12)'],
    purple: ['rgba(255,140,0,0.2)', 'rgba(204,34,0,0.18)', 'rgba(255,69,0,0.12)'],
    mixed: ['rgba(255,215,0,0.2)', 'rgba(255,140,0,0.18)', 'rgba(204,34,0,0.12)'],
    yellow: ['rgba(255,215,0,0.25)', 'rgba(255,140,0,0.2)', 'rgba(255,69,0,0.15)'],
    cyan: ['rgba(255,215,0,0.2)', 'rgba(255,140,0,0.18)', 'rgba(139,30,0,0.12)'],
    red: ['rgba(239,68,68,0.25)', 'rgba(220,38,38,0.2)', 'rgba(185,28,28,0.15)'],
  };
  const palette = colors[color] || colors.molten;

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-10">
      {[...Array(count)].map((_, i) => {
        const size = 8 + Math.random() * 30;
        const left = Math.random() * 100;
        const delay = Math.random() * 8;
        const duration = 8 + Math.random() * 7;
        return (
          <div key={i} style={{
            position: 'absolute',
            bottom: '-30px',
            left: `${left}%`,
            width: `${size}px`,
            height: `${size}px`,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${palette[i % palette.length]} 0%, transparent 70%)`,
            animation: `gasBubble ${duration}s ${delay}s ease-out infinite`,
            filter: 'blur(1px)',
          }} />
        );
      })}
    </div>
  );
};

// Toxic drips from the ceiling — molten color
export const ToxicDrips = ({ count = 6, color = '#ff8c00' }) => (
  <div className="absolute inset-x-0 top-0 pointer-events-none z-20 h-full overflow-hidden">
    {[...Array(count)].map((_, i) => (
      <div key={i} style={{
        position: 'absolute',
        top: '-10px',
        left: `${10 + (i * (80 / count)) + Math.random() * 10}%`,
        width: '2px',
        height: '30px',
        background: `linear-gradient(180deg, ${color}80 0%, ${color}20 60%, transparent 100%)`,
        borderRadius: '0 0 2px 2px',
        animation: `toxicDrip ${3 + Math.random() * 4}s ${Math.random() * 3}s ease-in infinite`,
        filter: `drop-shadow(0 0 4px ${color}40)`,
      }} />
    ))}
  </div>
);

// Sewer pipe border frame — molten brackets
export const SewerFrame = ({ color = 'rgba(255,140,0,' }) => (
  <>
    <div className="absolute top-0 left-0 right-0 h-1 z-30" style={{
      background: `linear-gradient(90deg, transparent 0%, ${color}0.3) 20%, ${color}0.1) 50%, ${color}0.3) 80%, transparent 100%)`,
    }} />
    <div className="absolute bottom-0 left-0 right-0 h-1 z-30" style={{
      background: `linear-gradient(90deg, transparent 0%, ${color}0.2) 20%, ${color}0.05) 50%, ${color}0.2) 80%, transparent 100%)`,
    }} />
    <div className="absolute top-0 left-0 w-px h-full z-30" style={{
      background: `linear-gradient(180deg, ${color}0.3) 0%, ${color}0.05) 50%, ${color}0.3) 100%)`,
    }} />
    <div className="absolute top-0 right-0 w-px h-full z-30" style={{
      background: `linear-gradient(180deg, ${color}0.3) 0%, ${color}0.05) 50%, ${color}0.3) 100%)`,
    }} />
    {/* Corner brackets */}
    <div className="absolute top-3 left-3 w-6 h-6 border-t-2 border-l-2 z-30" style={{ borderColor: `${color}0.4)` }} />
    <div className="absolute top-3 right-3 w-6 h-6 border-t-2 border-r-2 z-30" style={{ borderColor: `${color}0.4)` }} />
    <div className="absolute bottom-3 left-3 w-6 h-6 border-b-2 border-l-2 z-30" style={{ borderColor: `${color}0.4)` }} />
    <div className="absolute bottom-3 right-3 w-6 h-6 border-b-2 border-r-2 z-30" style={{ borderColor: `${color}0.4)` }} />
  </>
);

// Glitch title component — molten gradient
export const GlitchTitle = ({ text, subtitle, color = '#ff8c00', size = 'text-4xl md:text-5xl lg:text-6xl' }) => (
  <motion.div
    initial={{ opacity: 0, y: -20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.8, ease: 'easeOut' }}
    className="text-center relative"
  >
    <h1
      className={`${size} font-black tracking-[0.15em] uppercase relative`}
      style={{
        fontFamily: "'Cinzel', serif",
        background: `linear-gradient(180deg, #ffd700 0%, ${color} 40%, #cc2200 80%, #8b1a00 100%)`,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        filter: `drop-shadow(0 0 15px ${color}80) drop-shadow(0 2px 4px rgba(0,0,0,0.8))`,
        animation: 'glitchFlicker 8s ease-in-out infinite',
      }}
      data-testid="cinematic-title"
    >
      {text}
    </h1>
    {subtitle && (
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5, duration: 1 }}
        className="text-sm md:text-base tracking-[0.3em] uppercase mt-2"
        style={{
          fontFamily: "'Cinzel', serif",
          color: 'rgba(255,160,64,0.5)',
        }}
      >
        {subtitle}
      </motion.p>
    )}
  </motion.div>
);

// Warning/status bar at the bottom — molten
export const StatusBar = ({ text = 'SYSTEM ACTIVE', color = '#ff8c00' }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ delay: 1.5 }}
    className="absolute bottom-6 md:bottom-6 bottom-[max(1.5rem,env(safe-area-inset-bottom))] left-1/2 -translate-x-1/2 z-30 flex items-center gap-2"
  >
    <div className="w-1.5 h-1.5 rounded-full" style={{
      background: color,
      boxShadow: `0 0 6px ${color}`,
      animation: 'warningPulse 2s ease-in-out infinite',
    }} />
    <span className="text-[10px] tracking-[0.3em] uppercase font-bold" style={{
      color: '#ffc870',
      fontFamily: "'Courier New', monospace",
      textShadow: '0 1px 2px rgba(0,0,0,0.95), 0 0 5px rgba(255,140,0,0.5)',
    }}>{text}</span>
  </motion.div>
);

// Persistent Total Fees counter — molten gold theme
export const TotalFeesCounter = ({ fees = 0, color = '#ff8c00' }) => {
  const formatted = fees.toLocaleString('de-DE');
  return (
    <div className="hidden md:flex fixed top-4 right-4 z-[200] items-center gap-3 px-5 py-2.5"
      style={{
        // Solid lore-themed plate so it stays vibrant against any background vignette
        background: 'linear-gradient(180deg, rgba(28,18,6,0.99) 0%, rgba(12,7,2,1) 100%)',
        border: '1.5px solid rgba(220,170,80,0.85)',
        borderRadius: '4px',
        boxShadow: `
          inset 0 1px 1px rgba(255,210,120,0.35),
          inset 0 -1px 1px rgba(0,0,0,0.7),
          0 0 0 2px rgba(8,4,2,1),
          0 0 0 3px rgba(180,140,55,0.7),
          0 0 28px rgba(255,160,40,0.5),
          0 4px 14px rgba(0,0,0,0.85)
        `,
      }}
      data-testid="total-fees-counter"
    >
      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{
        background: 'radial-gradient(circle at 30% 30%, #fff5c0 0%, #ffd700 30%, #ff8c00 100%)',
        boxShadow: '0 0 10px #ffd700, 0 0 20px rgba(255,140,0,0.8)',
        animation: 'warningPulse 1.5s ease-in-out infinite',
      }} />
      <div className="flex flex-col leading-none">
        <span className="text-[10px] tracking-[0.3em] uppercase font-bold mb-0.5" style={{
          color: '#ffc870',
          fontFamily: "'Courier New', monospace",
          textShadow: '0 1px 2px rgba(0,0,0,1), 0 0 6px rgba(255,180,60,0.6)',
        }}>TOTAL FEES</span>
        <span className="text-2xl font-black tracking-wide" style={{
          fontFamily: "'Impact', 'Arial Black', sans-serif",
          color: '#ffffff',
          textShadow: '0 0 12px rgba(255,200,80,1), 0 0 22px rgba(255,140,0,0.75), 0 1px 2px rgba(0,0,0,1), 0 2px 4px rgba(0,0,0,0.95)',
          WebkitTextStroke: '0.5px rgba(255,200,80,0.85)',
        }}>{formatted}</span>
      </div>
    </div>
  );
};
